import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  CreditCard, Building2, Search, Edit, Save, Euro, Calendar, 
  CheckCircle, XCircle, AlertTriangle, Loader2, Crown, Gift
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import { API } from "@/App";

const SubscriptionsManager = ({ plans = [] }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Grant free plan state
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantingBusiness, setGrantingBusiness] = useState(null);
  const [grantForm, setGrantForm] = useState({
    plan_id: "",
    duration_months: 12,
    notes: ""
  });
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/superadmin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(res.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Errore nel caricamento abbonamenti");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sub) => {
    setEditingSubscription({ ...sub });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingSubscription) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/superadmin/subscriptions/${editingSubscription.business_id}`,
        {
          plan_id: editingSubscription.plan_id,
          is_active: editingSubscription.is_active,
          status: editingSubscription.subscription_status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Abbonamento aggiornato");
      setEditDialogOpen(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const openGrantDialog = (sub) => {
    setGrantingBusiness(sub);
    setGrantForm({
      plan_id: plans[0]?.plan_id || "",
      duration_months: 12,
      notes: ""
    });
    setGrantDialogOpen(true);
  };

  const handleGrantFreePlan = async () => {
    if (!grantingBusiness || !grantForm.plan_id) return;
    
    setGranting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/superadmin/subscriptions/${grantingBusiness.business_id}/grant-free`,
        grantForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message || "Piano gratuito assegnato!");
      setGrantDialogOpen(false);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'assegnazione");
    } finally {
      setGranting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr.replace('Z', '')), "dd/MM/yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatusBadge = (sub) => {
    if (!sub.is_active) {
      return <Badge className="bg-slate-100 text-slate-700"><XCircle className="h-3 w-3 mr-1" />Disattivata</Badge>;
    }
    
    const status = sub.subscription_status;
    if (status === "active") {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Attivo</Badge>;
    }
    if (status === "past_due") {
      return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" />Scaduto</Badge>;
    }
    if (status === "trialing") {
      return <Badge className="bg-blue-100 text-blue-700"><Crown className="h-3 w-3 mr-1" />Trial</Badge>;
    }
    if (status === "canceled") {
      return <Badge className="bg-orange-100 text-orange-700"><XCircle className="h-3 w-3 mr-1" />Cancellato</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">Gratuito</Badge>;
  };

  const getExpirationInfo = (sub) => {
    if (!sub.current_period_end) return null;
    
    try {
      const endDate = parseISO(sub.current_period_end.replace('Z', ''));
      const daysLeft = differenceInDays(endDate, new Date());
      
      if (daysLeft < 0) {
        return <span className="text-red-600 font-medium">Scaduto da {Math.abs(daysLeft)} giorni</span>;
      }
      if (daysLeft <= 7) {
        return <span className="text-orange-600 font-medium">Scade tra {daysLeft} giorni</span>;
      }
      return <span className="text-slate-600">{formatDate(sub.current_period_end)}</span>;
    } catch {
      return null;
    }
  };

  // Calculate stats
  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.is_active && s.subscription_status === "active").length,
    trial: subscriptions.filter(s => s.subscription_status === "trialing").length,
    pastDue: subscriptions.filter(s => s.subscription_status === "past_due").length,
    totalRevenue: subscriptions.reduce((sum, s) => sum + (s.plan_price || 0), 0)
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = sub.business_name?.toLowerCase().includes(term) ||
                          sub.owner_email?.toLowerCase().includes(term) ||
                          sub.owner_name?.toLowerCase().includes(term);
    const matchesPlan = filterPlan === "all" || sub.plan_id === filterPlan;
    const matchesStatus = filterStatus === "all" || 
                          (filterStatus === "active" && sub.is_active && sub.subscription_status === "active") ||
                          (filterStatus === "inactive" && !sub.is_active) ||
                          (filterStatus === "past_due" && sub.subscription_status === "past_due");
    
    return matchesSearch && matchesPlan && matchesStatus;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-slate-500">Caricamento abbonamenti...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-sm text-slate-500">Aziende Totali</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            <p className="text-sm text-slate-500">Attivi</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.trial}</p>
            <p className="text-sm text-slate-500">In Trial</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{stats.pastDue}</p>
            <p className="text-sm text-slate-500">Scaduti</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{formatCurrency(stats.totalRevenue)}</p>
            <p className="text-sm text-slate-500">MRR Mensile</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Gestione Abbonamenti</CardTitle>
                <CardDescription>
                  Controlla pagamenti e scadenze delle aziende
                </CardDescription>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-[150px]" data-testid="filter-plan">
                  <SelectValue placeholder="Piano" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i piani</SelectItem>
                  {plans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="active">Attivi</SelectItem>
                  <SelectItem value="inactive">Disattivati</SelectItem>
                  <SelectItem value="past_due">Scaduti</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-[180px]"
                  data-testid="search-subscription"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredSubscriptions.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna azienda trovata</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Azienda</TableHead>
                    <TableHead>Proprietario</TableHead>
                    <TableHead>Piano</TableHead>
                    <TableHead className="text-right">Prezzo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.business_id} data-testid={`sub-row-${sub.business_id}`}>
                      <TableCell>
                        <div className="font-medium">{sub.business_name}</div>
                        <div className="text-xs text-slate-500">
                          Dal {formatDate(sub.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{sub.owner_name}</div>
                        <div className="text-xs text-slate-500">{sub.owner_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{sub.plan_name}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(sub.plan_price)}/mese
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(sub)}
                      </TableCell>
                      <TableCell>
                        {getExpirationInfo(sub) || (
                          <span className="text-slate-400">-</span>
                        )}
                        {sub.cancel_at_period_end && (
                          <Badge variant="outline" className="ml-2 text-orange-600">
                            Non si rinnova
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openGrantDialog(sub)}
                            title="Regala piano gratuito"
                            data-testid={`grant-free-${sub.business_id}`}
                          >
                            <Gift className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(sub)}
                            data-testid={`edit-sub-${sub.business_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Abbonamento</DialogTitle>
            <DialogDescription>
              {editingSubscription?.business_name}
            </DialogDescription>
          </DialogHeader>
          
          {editingSubscription && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Piano</Label>
                <Select 
                  value={editingSubscription.plan_id} 
                  onValueChange={(v) => setEditingSubscription({...editingSubscription, plan_id: v})}
                >
                  <SelectTrigger data-testid="edit-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(p => (
                      <SelectItem key={p.plan_id} value={p.plan_id}>
                        {p.name} - {formatCurrency(p.price)}/mese
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Stato Abbonamento</Label>
                <Select 
                  value={editingSubscription.subscription_status} 
                  onValueChange={(v) => setEditingSubscription({...editingSubscription, subscription_status: v})}
                >
                  <SelectTrigger data-testid="edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Attivo</SelectItem>
                    <SelectItem value="trialing">Trial</SelectItem>
                    <SelectItem value="past_due">Scaduto</SelectItem>
                    <SelectItem value="canceled">Cancellato</SelectItem>
                    <SelectItem value="none">Nessuno (Gratuito)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Azienda Attiva</Label>
                  <p className="text-xs text-slate-500">Disattiva per bloccare accesso</p>
                </div>
                <Button
                  type="button"
                  variant={editingSubscription.is_active ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditingSubscription({
                    ...editingSubscription, 
                    is_active: !editingSubscription.is_active
                  })}
                  data-testid="toggle-active"
                >
                  {editingSubscription.is_active ? (
                    <><CheckCircle className="h-4 w-4 mr-2" />Attiva</>
                  ) : (
                    <><XCircle className="h-4 w-4 mr-2" />Disattivata</>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-subscription">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Free Plan Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              Regala Piano Gratuito
            </DialogTitle>
            <DialogDescription>
              Assegna un piano gratuito a <span className="font-semibold">{grantingBusiness?.business_name}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Piano da Assegnare</Label>
              <Select 
                value={grantForm.plan_id} 
                onValueChange={(v) => setGrantForm({...grantForm, plan_id: v})}
              >
                <SelectTrigger data-testid="grant-plan-select">
                  <SelectValue placeholder="Seleziona piano" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(p => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name} - (valore {formatCurrency(p.price)}/mese)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Durata (mesi)</Label>
              <Select 
                value={String(grantForm.duration_months)} 
                onValueChange={(v) => setGrantForm({...grantForm, duration_months: parseInt(v)})}
              >
                <SelectTrigger data-testid="grant-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mese</SelectItem>
                  <SelectItem value="3">3 mesi</SelectItem>
                  <SelectItem value="6">6 mesi</SelectItem>
                  <SelectItem value="12">12 mesi (1 anno)</SelectItem>
                  <SelectItem value="24">24 mesi (2 anni)</SelectItem>
                  <SelectItem value="36">36 mesi (3 anni)</SelectItem>
                  <SelectItem value="120">120 mesi (10 anni)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                placeholder="Es: Cliente speciale, promozione lancio..."
                value={grantForm.notes}
                onChange={(e) => setGrantForm({...grantForm, notes: e.target.value})}
                data-testid="grant-notes"
              />
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="text-green-800">
                <strong>Riepilogo:</strong> Il piano <strong>{plans.find(p => p.plan_id === grantForm.plan_id)?.name || "..."}</strong> sarà 
                attivato gratuitamente per <strong>{grantForm.duration_months} mesi</strong>.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleGrantFreePlan} 
              disabled={granting || !grantForm.plan_id}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-grant-free"
            >
              {granting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Gift className="h-4 w-4 mr-2" />
              )}
              Assegna Gratuitamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionsManager;
